import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useDevice } from 'device'
import { constants, devicePx } from 'helpers'
import { isAnyModalOpen } from 'modals'
import { useAb } from 'hooks'

import { subscribe, unsubscribe, closeNotification } from './manager'


type NotificationProps = {
  id: number
  component: React.ComponentType<any>
  componentProps: { [key: string]: any }
}

const Notification: React.FunctionComponent<NotificationProps> = ({ id, component, componentProps }) => {
  const handleClose = useCallback(() => {
    closeNotification(id)
  }, [ id ])

  return React.createElement(component, {
    ...componentProps,
    closeNotification: handleClose,
  })
}


type NotificationsProps = {
  className?: string
  templates?: {
    [key: string]: React.ComponentType<any>
  }
}

const Notifications: React.FunctionComponent<NotificationsProps> = (props) => {
  const { className, templates } = props

  const isCartAutoOpenEnabled = useAb(constants.abTests.cartNotificationDesignUpdate) === 'a'

  const [ notifications, setNotifications ] = useState([])

  const { isMobile } = useDevice()
  const ref = useRef<HTMLDivElement>()
  const prevOffsetRef = useRef<number>()
  const hasNotificationsRef = useRef<boolean>()

  hasNotificationsRef.current = notifications.length > 0

  const setOffset = (newOffset: number) => {
    if (prevOffsetRef.current !== newOffset) {
      ref.current.style.transform = `translateY(${newOffset}px)`
      prevOffsetRef.current = newOffset
    }
  }

  // Set offset to keep notifications below the main header
  const updateOffset = () => {
    // Do not update offset if there are no notifications
    if (!ref.current || !hasNotificationsRef.current) {
      return
    }

    // Reset offset if needed when any modal is open or user scrolled more than 1/4 of the screen
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
    if (isAnyModalOpen() || scrollTop > innerHeight / 4) {
      setOffset(0)
      return
    }

    // Update offset if needed when main header is visible
    const mainHeaderElement = document.querySelector('#main-header')
    if (mainHeaderElement) {
      const mainHeaderRect = mainHeaderElement.getBoundingClientRect()

      const margin = (isMobile ? 4 : -8) - 16 // icon margin - position top
      const offset = Math.max(mainHeaderRect.bottom + devicePx(margin), 0)
      setOffset(offset)
    }
  }

  useEffect(() => {
    setOffset(0)

    if (isCartAutoOpenEnabled) {
      return
    }

    const interval = setInterval(() => {
      requestAnimationFrame(updateOffset)
    }, 200)

    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ ])

  useEffect(() => {
    subscribe(setNotifications)

    return () => {
      unsubscribe(setNotifications)
    }
  }, [])

  return (
    <div
      className={className}
      ref={ref}
    >
      {
        notifications.map(({ id, name, props }) => {
          const component = templates[name]

          return (
            <Notification
              key={id}
              id={id}
              component={component}
              componentProps={props}
            />
          )
        })
      }
    </div>
  )
}


export default React.memo(Notifications)
